import React, { useEffect, useState } from "react";
import {
  Modal,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Paper,
  Checkbox,
  TextField,
  Grid,
  Stack,
  Divider,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Alert
} from "@mui/material";
import useSignedUrl from "../../utils/useSignedUrl";
import getInitials from "../../utils/getInitials";
import {
  editChallengeHandler,
  getSingleChallengeWithDateRange
} from "../../api/userApi";
import { fetchAllChallenges } from "../../features/users/userSlice";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

const ImageItem = ({ img }) => {
  const imgSrc = useSignedUrl(
    img,
    process.env.REACT_APP_BACKEND_PROFILE_IMAGE_LOCATION
  );

  return (
    <Box
      component="img"
      borderRadius="50%"
      height={40}
      width={40}
      src={imgSrc}
      alt="avatar"
    />
  );
};

const LeaderboardModal = ({
  leaderboardData,
  open,
  selectedChallenge,
  setOpen,
  isFromSubAdmin,
  currentCoach,
  referenceId
}) => {
  const [loading, setLoading] = useState(false);
  const [dateRangeLoading, setDateRangeLoading] = useState(false);
  const [filteredChallengeData, setFilteredChallengeData] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const selectedData =
    filteredChallengeData ||
    leaderboardData?.find((item) => item?._id === selectedChallenge?._id);

  const [startDate, setStartDate] = useState(
    selectedChallenge?.start_date
      ? dayjs.utc(selectedChallenge.start_date).format("YYYY-MM-DD")
      : ""
  );
  const [endDate, setEndDate] = useState(
    selectedChallenge?.end_date
      ? dayjs.utc(selectedChallenge.end_date).format("YYYY-MM-DD")
      : ""
  );

  const dispatch = useDispatch();

  // Reset filtered data when modal opens with new challenge
  useEffect(() => {
    if (open && selectedChallenge) {
      setFilteredChallengeData(null);
      setStartDate(
        selectedChallenge?.start_date
          ? dayjs.utc(selectedChallenge.start_date).format("YYYY-MM-DD")
          : ""
      );
      setEndDate(
        selectedChallenge?.end_date
          ? dayjs.utc(selectedChallenge.end_date).format("YYYY-MM-DD")
          : ""
      );
    }
  }, [open, selectedChallenge]);

  const fetchChallengeWithDateRange = async () => {
    if (!selectedChallenge?._id || !startDate || !endDate) return;

    setDateRangeLoading(true);
    try {
      const params = {
        challengeId: selectedChallenge._id,
        startDate,
        endDate,
        referenceId: currentCoach ? currentCoach : ""
      };

      const response = await getSingleChallengeWithDateRange(params);
      if (response?.challenge) {
        setFilteredChallengeData(response.challenge);
      }
    } catch (error) {
      console.error("Error fetching challenge with date range:", error);
      setSnackbarMessage(error.message || "Failed to fetch challenge");
      setOpenSnackbar(true);
    } finally {
      setDateRangeLoading(false);
    }
  };

  const handleDateChange = (field, value) => {
    if (field === "startDate") {
      setStartDate(value);
    } else {
      setEndDate(value);
    }
  };

  const handleApplyDateRange = () => {
    fetchChallengeWithDateRange();
  };

  const handleResetDateRange = () => {
    setStartDate(
      selectedChallenge?.start_date
        ? dayjs.utc(selectedChallenge.start_date).format("YYYY-MM-DD")
        : ""
    );
    setEndDate(
      selectedChallenge?.end_date
        ? dayjs.utc(selectedChallenge.end_date).format("YYYY-MM-DD")
        : ""
    );
    setFilteredChallengeData(null);
  };

  const getAveragePoints = () => {
    const userPoints = isFromSubAdmin
      ? selectedData?.userPoints
      : selectedData?.userPoints?.filter(
          (point) =>
            !selectedData?.excluded_users?.includes(point.userId) &&
            point.totalPoints > 0
        );

    if (!userPoints || userPoints.length === 0) return 0;

    const total = userPoints.reduce(
      (sum, user) => sum + (user.totalPoints || 0),
      0
    );
    const average = total / userPoints.length;
    return Math.round(average || 0);
  };
  const getParticipantCount = () => {
    const userPoints = isFromSubAdmin
      ? selectedData?.userPoints
      : selectedData?.userPoints?.filter(
          (point) =>
            !selectedData?.excluded_users?.includes(point.userId) &&
            point.totalPoints > 0
        );

    return userPoints?.length || 0;
  };
  const getTeamPointsWithLogBoost = () => {
    const totalParticipants = getParticipantCount();
    // totalPoints + (10 * ln(participants))
    const result =
      getAveragePoints() +
      10 * (totalParticipants ? Math.log(totalParticipants) : 0);

    return Math.round(result || 0); // or keep decimals if you want
  };

  const challengeStartDate = selectedChallenge?.start_date
    ? dayjs.utc(selectedChallenge.start_date).format("YYYY-MM-DD")
    : "";
  const challengeEndDate = selectedChallenge?.end_date
    ? dayjs.utc(selectedChallenge.end_date).format("YYYY-MM-DD")
    : "";

  const handleClose = () => {
    setOpen(false);
    setFilteredChallengeData(null);
  };

  const handleCheckboxChange = async (user) => {
    setLoading(true);
    try {
      await editChallengeHandler({
        userId: user?.userId,
        id: selectedChallenge?._id,
        isFromAdmin: true
      });

      // Refresh the challenge data
      if (filteredChallengeData) {
        // If we're viewing filtered data, refresh the filtered view
        await fetchChallengeWithDateRange();
      } else {
        // If we're viewing original data, refresh all challenges
        dispatch(fetchAllChallenges(referenceId));
      }
    } catch (error) {
      console.error("Error updating user exclusion status:", error);
    } finally {
      setLoading(false);
    }
  };

  const getLeaderPosition = (position, index) => {
    switch (index) {
      case 0:
        return "🥇";
      case 1:
        return "🥈";
      case 2:
        return "🥉";
      default:
        return `${position}. `;
    }
  };

  const isDateRangeChanged = () => {
    const originalStartDate = selectedChallenge?.start_date
      ? dayjs.utc(selectedChallenge.start_date).format("YYYY-MM-DD")
      : "";
    const originalEndDate = selectedChallenge?.end_date
      ? dayjs.utc(selectedChallenge.end_date).format("YYYY-MM-DD")
      : "";

    return startDate !== originalStartDate || endDate !== originalEndDate;
  };

  return (
    <Modal
      disableAutoFocus
      disableEnforceFocus
      open={open}
      onClose={handleClose}
      aria-labelledby="leaderboard-modal"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <Paper
        elevation={24}
        sx={{
          width: "100%",
          maxWidth: 600,
          maxHeight: "90vh",
          overflow: "auto",
          borderRadius: 3,
          p: 3
        }}
      >
        <Snackbar
          open={openSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          autoHideDuration={2000}
          onClose={() => setOpenSnackbar(false)}
        >
          <Alert severity="error" sx={{ width: "100%" }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
        <Typography
          variant="h5"
          component="h2"
          gutterBottom
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2
          }}
        >
          Leaderboard
          {dateRangeLoading && <CircularProgress size={20} />}
        </Typography>
        <Typography
          variant="body2"
          gutterBottom
          sx={{ textAlign: "center", mb: 5 }}
        >
          The leaderboard is populated with scores <br /> <b> a) </b> the day after the start
          date (since "today's" points display "tomorrow"), and <br /> <b> b) </b> with any paid
          participant that has logged food within the challenge dates.
        </Typography>
        {!referenceId && (
          <>
            {/* Date Range Controls */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) =>
                    handleDateChange("startDate", e.target.value)
                  }
                  InputLabelProps={{
                    shrink: true
                  }}
                  inputProps={{
                    min: challengeStartDate,
                    max: challengeEndDate
                  }}
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => handleDateChange("endDate", e.target.value)}
                  InputLabelProps={{
                    shrink: true
                  }}
                  inputProps={{
                    min: startDate || challengeStartDate,
                    max: challengeEndDate
                  }}
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Button
                variant="contained"
                onClick={handleApplyDateRange}
                disabled={!startDate || !endDate || dateRangeLoading}
                size="small"
                sx={{ flexGrow: 1 }}
              >
                {dateRangeLoading ? "Loading..." : "Apply Date Range"}
              </Button>
              {isDateRangeChanged() && (
                <Button
                  variant="outlined"
                  onClick={handleResetDateRange}
                  size="small"
                  sx={{ flexGrow: 1 }}
                >
                  Reset to Original
                </Button>
              )}
            </Stack>

            {/* Date Range Indicator */}
            {filteredChallengeData && (
              <Box sx={{ mb: 2, p: 1, bgcolor: "info.light", borderRadius: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ color: "info.contrastText", textAlign: "center" }}
                >
                  Showing results for: {dayjs(startDate).format("MMM DD, YYYY")}{" "}
                  - {dayjs(endDate).format("MMM DD, YYYY")}
                </Typography>
              </Box>
            )}

            {/* Statistics */}
            <Stack
              direction="row"
              spacing={4}
              justifyContent="center"
              sx={{ mb: 2 }}
            >
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", color: "primary.main" }}
                >
                  {getTeamPointsWithLogBoost()}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Team Score
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", color: "primary.main" }}
                >
                  {getAveragePoints()}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Average Score
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", color: "primary.main" }}
                >
                  {getParticipantCount()}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Team Size
                </Typography>
              </Box>
            </Stack>

            <Divider sx={{ mt: 2 }} />
          </>
        )}
        <List sx={{ width: "100%" }}>
          {(isFromSubAdmin
            ? selectedData?.userPoints
            : selectedData?.userPoints?.filter(
                (point) => !selectedData?.excluded_users?.includes(point.userId)
              )
          )?.map((user, index) => (
            <ListItem
              key={user._id}
              sx={{
                mb: 2,
                display: "flex",
                gap: 2,
                backgroundColor:
                  index < 3 ? "rgba(0, 0, 0, 0.02)" : "transparent",
                borderRadius: 2,
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                  transform: "scale(1.02)"
                }
              }}
            >
              {isFromSubAdmin && (
                <Checkbox
                  onChange={(e) => handleCheckboxChange(user, e.target.checked)}
                  checked={!user?.isExcluded}
                  disabled={loading} // Disable while API call is in progress
                />
              )}
              <ListItemAvatar
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                {!user?.isExcluded && (
                  <ListItemText
                    primary={
                      <Typography variant="h6" component="span">
                        {getLeaderPosition(user?.position, index)}
                      </Typography>
                    }
                  />
                )}
                {user?.profilePic ? (
                  <ImageItem
                    img={user?.profilePic}
                    alt={user?.displayName || user?.fullName}
                  />
                ) : (
                  <Avatar sx={{ width: 40, height: 40 }}>
                    {getInitials(user?.displayName || user?.fullName)}
                  </Avatar>
                )}
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant="body" component="span">
                    {user?.displayName || user?.fullName}{" "}
                    {user?.isSelfExcluded ? "(self excluded)" : ""}
                  </Typography>
                }
              />
              <ListItemSecondaryAction>
                <Chip
                  label={`${
                    Number.isInteger(+user?.totalPoints)
                      ? +user?.totalPoints
                      : (+user?.totalPoints)?.toFixed(2)
                  } pts`}
                  color={index < 3 ? "primary" : "default"}
                  sx={{
                    fontWeight: "bold",
                    fontSize: "1rem"
                  }}
                />
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Paper>
    </Modal>
  );
};

export default LeaderboardModal;
